import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  AttachMoney as AttachMoneyIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
  RefreshRounded as RefreshRoundedIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeAccionProductos } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

const PROGRAMA_LISTAS = 'listasprecios';

/** Mismos estilos de campo que `Motores/informemotores.js` (INPUT_SX). */
const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

const outlineBtnSx = {
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const primaryBtnSx = {
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  boxShadow: 'none',
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const MantenimientoListasPrecios = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  /* Contexto alineado con Motores/informemotores.js: controller expone nombreNegocio, usuarioLog, claveLog, roleWeb, etc. */
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_LISTAS);
  const puedeModificar = puedeAccionProductos(roleDetalles, PROGRAMA_LISTAS, 'modificar');
  const puedeEliminar = puedeAccionProductos(roleDetalles, PROGRAMA_LISTAS, 'borrar');

  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [descuentoPrecioFinal, setDescuentoPrecioFinal] = useState('');
  const [listas, setListas] = useState([]);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [listaSeleccionada, setListaSeleccionada] = useState(null);
  const [dialogoConfirmacion, setDialogoConfirmacion] = useState(false);
  const [listaAEliminar, setListaAEliminar] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargarListas = useCallback(async () => {
    if (!selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_listarlistasprecios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setListas(data.listas || []);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al cargar listas de precios');
      }
    } catch (error) {
      showError('Error de conexión al cargar listas de precios');
    } finally {
      setCargando(false);
    }
  }, [selectedCompany, usuarioLog, claveLog, showError]);

  useEffect(() => {
    cargarListas();
  }, [cargarListas]);

  const convertirAMayusculas = (texto) => (texto || '').toUpperCase();

  const validarFormulario = () => {
    if (!codigo.trim()) {
      showError('El código es requerido');
      return false;
    }
    if (!descripcion.trim()) {
      showError('La descripción es requerida');
      return false;
    }
    const desc = parseFloat(descuentoPrecioFinal);
    if (descuentoPrecioFinal.trim() !== '' && (isNaN(desc) || desc < 0 || desc > 100)) {
      showError('El descuento sobre precio final debe ser un porcentaje entre 0 y 100');
      return false;
    }
    return true;
  };

  const limpiarFormulario = () => {
    setCodigo('');
    setDescripcion('');
    setDescuentoPrecioFinal('');
    setListaSeleccionada(null);
    setModoEdicion(false);
  };

  const handleGuardar = async () => {
    if (!validarFormulario() || !puedeModificar) return;

    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_guardarlistaprecio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          codigo: convertirAMayusculas(codigo.trim()),
          descripcion: descripcion.trim(),
          descuento_precio_final: descuentoPrecioFinal.trim() === '' ? 0 : parseFloat(descuentoPrecioFinal) || 0,
          usuario: usuarioLog || 'Sistema',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Lista de precios guardada exitosamente');
        limpiarFormulario();
        cargarListas();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al guardar la lista de precios');
      }
    } catch (error) {
      showError('Error de conexión al guardar');
    }
  };

  const handleModificar = async () => {
    if (!listaSeleccionada || !validarFormulario() || !puedeModificar) return;

    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_modificarlistaprecio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          id: listaSeleccionada.id,
          codigo: convertirAMayusculas(codigo.trim()),
          descripcion: descripcion.trim(),
          descuento_precio_final: descuentoPrecioFinal.trim() === '' ? 0 : parseFloat(descuentoPrecioFinal) || 0,
          usuario: usuarioLog || 'Sistema',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Lista de precios modificada exitosamente');
        limpiarFormulario();
        cargarListas();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al modificar la lista de precios');
      }
    } catch (error) {
      showError('Error de conexión al modificar');
    }
  };

  const handleEliminar = async () => {
    if (!listaAEliminar || !puedeEliminar) return;

    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_eliminarlistaprecio', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          id: listaAEliminar.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Lista de precios eliminada exitosamente');
        setDialogoConfirmacion(false);
        setListaAEliminar(null);
        cargarListas();
        if (listaSeleccionada && listaAEliminar && listaSeleccionada.id === listaAEliminar.id) {
          limpiarFormulario();
        }
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al eliminar la lista de precios');
        setDialogoConfirmacion(false);
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
      setDialogoConfirmacion(false);
    }
  };

  const seleccionarLista = (lista) => {
    setListaSeleccionada(lista);
    setCodigo(lista.codigo || '');
    setDescripcion(lista.descripcion || '');
    setDescuentoPrecioFinal(
      lista.descuento_precio_final != null && lista.descuento_precio_final !== ''
        ? String(lista.descuento_precio_final)
        : '',
    );
    setModoEdicion(true);
  };

  const listaEnUso = (lista) => !!lista?.en_uso;

  const confirmarEliminar = (lista) => {
    if (!puedeEliminar) {
      showError('No tiene permiso para eliminar listas de precios.');
      return;
    }
    if (listaEnUso(lista)) {
      showError(
        'No se puede eliminar: ya existen precios con esta lista. Solo se permite eliminar cuando no tiene precios asociados.',
      );
      return;
    }
    setListaAEliminar(lista);
    setDialogoConfirmacion(true);
  };

  const encabezadoSeccionSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const iconoTituloSx = {
    width: 30,
    height: 30,
    borderRadius: 1.2,
    bgcolor: FP.color.accentBlueSoft,
    color: FP.color.accentBlue,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const deleteOutlineBtnSx = {
    ...outlineBtnSx,
    borderColor: FP.color.danger,
    color: FP.color.danger,
    '&:hover': {
      bgcolor: FP.color.dangerSoft,
      borderColor: FP.color.danger,
    },
    '&.Mui-disabled': {
      borderColor: FP.color.border,
      color: FP.color.textMuted,
    },
  };

  const formHeaderTitleSx = {
    fontWeight: 700,
    color: FP.color.textPrimary,
    fontSize: '0.98rem',
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const mostrarAccionesListas = puedeModificar || puedeEliminar;
  const listasAnchosColumnas = mostrarAccionesListas
    ? ['8%', '14%', '36%', '12%', '14%', '16%']
    : ['10%', '16%', '44%', '14%', '16%'];
  const listasColgroup = (
    <colgroup>
      {listasAnchosColumnas.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  );
  const listasTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: { xs: 720, sm: 880 },
  };

  if (!selectedCompany?.db_empresa) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Listas de precios" subtitle="Seleccione una empresa activa" showQuickActions={false} />
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 520,
              width: '100%',
              ...cardShellSx,
              p: { xs: 3, md: 4 },
              textAlign: 'center',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 48, color: FP.color.textMuted, mb: 2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 1 }}>
              Sin empresa seleccionada
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
              Elija una empresa en la barra superior para administrar listas de precios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Listas de precios" subtitle="Permisos · Acceso restringido" showQuickActions={false} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              maxWidth: 560,
              width: '100%',
              ...cardShellSx,
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso de lectura del programa «listasprecios» en roles (módulo Productos).
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Listas de precios"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Mantenimiento de productos`
            : 'Mantenimiento de productos'
        }
        cta={
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => cargarListas()}
            disabled={cargando}
            sx={{
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              bgcolor: '#FFFFFF',
              borderRadius: 1.4,
              px: 2,
              height: 38,
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Actualizar
          </Button>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={encabezadoSeccionSx}>
            <Box sx={iconoTituloSx}>
              <AttachMoneyIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={formHeaderTitleSx}>Información</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600, display: { xs: 'none', lg: 'block' } }}>
              Código · Descripción · Descuento % · Estado en uso
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.875rem', maxWidth: 900 }}>
              Gestione las listas de precios que podrán asignarse a los productos. No se puede eliminar una lista si ya
              existen precios con la lista; solo se permite eliminar cuando no tiene precios asociados.
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...cardShellSx, overflow: 'hidden' }}>
              <Box sx={encabezadoSeccionSx}>
                <Box sx={iconoTituloSx}>{modoEdicion ? <EditIcon sx={{ fontSize: 18 }} /> : <AddIcon sx={{ fontSize: 18 }} />}</Box>
                <Typography sx={formHeaderTitleSx}>{modoEdicion ? 'Modificar lista' : 'Nueva lista de precios'}</Typography>
              </Box>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={2.5}>
                  {modoEdicion && listaSeleccionada && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="ID"
                        value={listaSeleccionada.id}
                        disabled
                        InputLabelProps={{ shrink: true }}
                        sx={INPUT_SX}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Código"
                      value={codigo}
                      onChange={(e) => setCodigo(convertirAMayusculas(e.target.value))}
                      required
                      inputProps={{ maxLength: 50 }}
                      disabled={!puedeModificar}
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Descripción"
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      required
                      inputProps={{ maxLength: 255 }}
                      disabled={!puedeModificar}
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Descuento sobre precio final (%)"
                      value={descuentoPrecioFinal}
                      onChange={(e) => setDescuentoPrecioFinal(e.target.value)}
                      type="number"
                      inputProps={{ step: '0.01', min: 0, max: 100 }}
                      placeholder="0"
                      helperText="Porcentaje de descuento aplicado al precio final (0-100)"
                      disabled={!puedeModificar}
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3, borderColor: FP.color.border }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <Button
                    variant="contained"
                    startIcon={modoEdicion ? <SaveIcon /> : <AddIcon />}
                    onClick={modoEdicion ? handleModificar : handleGuardar}
                    disabled={!puedeModificar}
                    fullWidth
                    sx={{ ...primaryBtnSx, height: 46 }}
                  >
                    {modoEdicion ? 'Modificar' : 'Guardar'}
                  </Button>
                  {modoEdicion && (
                    <Button
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      onClick={() => confirmarEliminar(listaSeleccionada)}
                      disabled={!puedeEliminar || listaEnUso(listaSeleccionada)}
                      fullWidth
                      sx={{ ...deleteOutlineBtnSx, height: 46 }}
                    >
                      Eliminar
                    </Button>
                  )}
                  <Button variant="outlined" startIcon={<ClearIcon />} onClick={limpiarFormulario} fullWidth sx={{ ...outlineBtnSx, height: 46 }}>
                    Limpiar
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card elevation={0} sx={{ ...cardShellSx, overflow: 'hidden' }}>
              <Box sx={encabezadoSeccionSx}>
                <Box sx={iconoTituloSx}>
                  <AttachMoneyIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography sx={formHeaderTitleSx}>Listado de listas de precios</Typography>
              </Box>
              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                {cargando ? (
                  <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                    Cargando…
                  </Typography>
                ) : listas.length === 0 ? (
                  <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                    No hay listas de precios registradas.
                  </Typography>
                ) : (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: `1px solid ${FP.color.border}`,
                      borderRadius: 2,
                      overflow: 'auto',
                    }}
                  >
                    <Table sx={listasTableSx}>
                      {listasColgroup}
                      <TableBody>
                        <TableRow
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            '& td': {
                              border: 'none',
                              borderBottom: `2px solid ${FP.color.border}`,
                              bgcolor: FP.color.surfaceAlt,
                            },
                          }}
                        >
                          <TableCell>
                            <Typography component="span" sx={cellHeadSx}>
                              ID
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography component="span" sx={cellHeadSx}>
                              Código
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography component="span" sx={cellHeadSx}>
                              Descripción
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              component="span"
                              sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}
                            >
                              Desc. %
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              component="span"
                              sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}
                            >
                              Estado
                            </Typography>
                          </TableCell>
                          {mostrarAccionesListas && (
                            <TableCell align="center" className="no-print">
                              <Typography
                                component="span"
                                sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}
                              >
                                Acciones
                              </Typography>
                            </TableCell>
                          )}
                        </TableRow>
                        {listas.map((lista) => (
                          <TableRow
                            key={lista.id}
                            hover
                            onClick={() => seleccionarLista(lista)}
                            sx={{
                              cursor: 'pointer',
                              bgcolor:
                                listaSeleccionada?.id === lista.id
                                  ? FP.color.accentBlueSoft
                                  : undefined,
                            }}
                          >
                            <TableCell align="left">{lista.id}</TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={lista.codigo}
                            >
                              {lista.codigo}
                            </TableCell>
                            <TableCell
                              align="left"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              title={lista.descripcion || '-'}
                            >
                              {lista.descripcion || '-'}
                            </TableCell>
                            <TableCell align="right">
                              {lista.descuento_precio_final != null &&
                              Number(lista.descuento_precio_final) !== 0
                                ? `${Number(lista.descuento_precio_final)}%`
                                : '-'}
                            </TableCell>
                            <TableCell align="center">
                              {listaEnUso(lista) ? (
                                <Chip
                                  label="En uso"
                                  size="small"
                                  sx={{
                                    bgcolor: FP.color.warningSoft,
                                    color: FP.color.warning,
                                    fontWeight: 600,
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="Disponible"
                                  size="small"
                                  sx={{
                                    bgcolor: FP.color.successSoft,
                                    color: FP.color.successDark,
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </TableCell>
                            {mostrarAccionesListas && (
                              <TableCell align="center" className="no-print">
                                {puedeModificar && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      seleccionarLista(lista);
                                    }}
                                    aria-label="Editar lista"
                                    sx={{
                                      color: FP.color.accentBlue,
                                      '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {puedeEliminar && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      confirmarEliminar(lista);
                                    }}
                                    disabled={listaEnUso(lista)}
                                    aria-label="Eliminar lista"
                                    sx={{
                                      color: FP.color.danger,
                                      '&:hover': { bgcolor: FP.color.dangerSoft },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog
          open={dialogoConfirmacion}
          onClose={() => {
            setDialogoConfirmacion(false);
            setListaAEliminar(null);
          }}
          PaperProps={{
            sx: {
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              boxShadow: FP.shadow.md,
              overflow: 'hidden',
            },
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              px: 3,
              py: 2,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
            }}
          >
            <Box sx={iconoTituloSx}>
              <DeleteIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }} component="span">
              Confirmar eliminación
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5, px: 3, pb: 1 }}>
            <Typography sx={{ color: FP.color.textPrimary }}>
              ¿Está seguro que desea eliminar la lista de precios &quot;{listaAEliminar?.descripcion}&quot; (código:{' '}
              {listaAEliminar?.codigo})?
            </Typography>
            {listaAEliminar && listaEnUso(listaAEliminar) && (
              <Typography sx={{ mt: 1.5, color: FP.color.warning }}>
                Ya existen precios con esta lista; no se puede eliminar. Solo se permite eliminar cuando no tiene precios asociados.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
            <Button
              onClick={() => {
                setDialogoConfirmacion(false);
                setListaAEliminar(null);
              }}
              sx={{ ...outlineBtnSx, height: 46 }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleEliminar}
              variant="contained"
              disabled={listaAEliminar && listaEnUso(listaAEliminar)}
              sx={{
                bgcolor: FP.color.danger,
                color: '#FFFFFF',
                borderRadius: 1.4,
                height: 46,
                px: 2.4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#BE123C', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
              }}
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MantenimientoListasPrecios;
